import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
    addEdge, useNodesState, useEdgesState,
    Controls, Background, MiniMap, MarkerType,
    Handle, Position, Panel, getBezierPath,
    EdgeLabelRenderer, BaseEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { flowsAPI, templatesAPI, whatsappAPI } from '../lib/api';
import {
    ArrowLeft, Save, Play, Pause, Plus, Trash2,
    Mail, Clock, GitBranch, Zap, X, FileText,
    MessageCircle, Tag, Filter, Workflow,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Edge com botão de deletar
// ---------------------------------------------------------------------------
const CustomEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style = {}, markerEnd, data }) => {
    const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
    return (
        <>
            <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
            <EdgeLabelRenderer>
                <div style={{ position: 'absolute', transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`, pointerEvents: 'all' }} className="nodrag nopan">
                    <button className="w-6 h-6 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-red-50 hover:border-red-400 transition-all shadow"
                        onClick={(e) => { e.stopPropagation(); data?.onDelete?.(id); }}>
                        <X className="w-3 h-3 text-gray-500 hover:text-red-500" />
                    </button>
                </div>
            </EdgeLabelRenderer>
        </>
    );
};

const edgeTypes = { custom: CustomEdge };

// ---------------------------------------------------------------------------
// Configuração dos tipos de nó — adaptados para Tayane Larini
// ---------------------------------------------------------------------------
const NODE_CONFIGS = {
    trigger: {
        icon: Zap, label: 'Gatilho', color: '#d97706',
        gradient: 'from-amber-600 to-amber-800', description: 'Inicia o fluxo',
    },
    wait: {
        icon: Clock, label: 'Aguardar', color: '#6366f1',
        gradient: 'from-indigo-500 to-indigo-700', description: 'Espera X dias/horas',
    },
    email: {
        icon: Mail, label: 'Enviar Email', color: '#0ea5e9',
        gradient: 'from-sky-500 to-sky-700', description: 'Dispara via Resend',
    },
    whatsapp: {
        icon: MessageCircle, label: 'WhatsApp', color: '#16a34a',
        gradient: 'from-green-600 to-green-800', description: 'Envia via Meta Cloud API',
    },
    condition: {
        icon: GitBranch, label: 'Condição', color: '#db2777',
        gradient: 'from-pink-500 to-pink-700', description: 'Ramifica o fluxo',
    },
    tag: {
        icon: Tag, label: 'Adicionar Tag', color: '#9333ea',
        gradient: 'from-purple-500 to-purple-700', description: 'Marca o contato',
    },
    filter: {
        icon: Filter, label: 'Filtro', color: '#64748b',
        gradient: 'from-slate-500 to-slate-700', description: 'Filtra contatos',
    },
};

// Gatilhos disponíveis (contexto Tayane Larini)
const TRIGGERS = [
    { value: 'lead_subscribed',        label: 'Novo cadastro (qualquer segmento)' },
    { value: 'lead_subscribed_mcp',    label: 'Novo cadastro — Mulheres com Propósito' },
    { value: 'lead_subscribed_ester',  label: 'Novo cadastro — Mesa de Ester' },
    { value: 'lead_subscribed_luto',   label: 'Novo cadastro — Do Luto ao Recomeço' },
    { value: 'lead_subscribed_devoc',  label: 'Novo cadastro — Devocional' },
    { value: 'lead_subscribed_musica', label: 'Novo cadastro — Música' },
    { value: 'lead_subscribed_livro',  label: 'Novo cadastro — Livro' },
    { value: 'payment_confirmed',      label: 'Compra confirmada' },
    { value: 'event_registered',       label: 'Inscrição em evento' },
    { value: 'waitlist_added',         label: 'Entrou na lista de espera' },
    { value: 'tag_added',              label: 'Tag adicionada' },
    { value: 'whatsapp_message',       label: 'Mensagem recebida (WhatsApp)' },
    { value: 'birthday',               label: 'Aniversário do lead' },
    { value: 'manual',                 label: 'Acionamento manual' },
];

// Delays disponíveis
const DELAYS = [
    { value: '30m', label: '30 minutos' },
    { value: '1h',  label: '1 hora' },
    { value: '6h',  label: '6 horas' },
    { value: '12h', label: '12 horas' },
    { value: '1d',  label: '1 dia' },
    { value: '2d',  label: '2 dias' },
    { value: '3d',  label: '3 dias' },
    { value: '7d',  label: '7 dias' },
    { value: '14d', label: '14 dias' },
    { value: '30d', label: '30 dias' },
];

// Segmentos
const SEGMENTOS = [
    { value: 'geral',                  label: 'Geral' },
    { value: 'mulheres_com_proposito', label: 'Mulheres com Propósito' },
    { value: 'mesa_de_ester',          label: 'Mesa de Ester' },
    { value: 'luto_ao_recomeco',       label: 'Do Luto ao Recomeço' },
    { value: 'devocional',             label: 'Devocional' },
    { value: 'musica',                 label: 'Música' },
    { value: 'livro',                  label: 'Livro' },
];

// ---------------------------------------------------------------------------
// Nó customizado
// ---------------------------------------------------------------------------
const CustomNode = ({ data, selected }) => {
    const config = NODE_CONFIGS[data.nodeType] || NODE_CONFIGS.trigger;
    const Icon = config.icon;

    return (
        <div className={`bg-white border-2 rounded-xl shadow-lg min-w-[200px] transition-all
            ${selected ? 'border-amber-500 shadow-amber-500/20' : 'border-gray-200 hover:border-gray-400'}`}>
            <Handle type="target" position={Position.Top} className="!bg-gray-400 !border-gray-200 !w-3 !h-3" />

            {/* Header */}
            <div className={`bg-gradient-to-r ${config.gradient} rounded-t-lg px-3 py-2 flex items-center gap-2`}>
                <Icon className="w-4 h-4 text-white" />
                <span className="text-xs font-bold text-white">{config.label}</span>
            </div>

            {/* Content */}
            <div className="px-3 py-2">
                <p className="text-xs text-gray-700 font-medium truncate max-w-[180px]">
                    {data.label || config.description}
                </p>
                {data.delay && (
                    <Badge variant="outline" className="mt-1 text-xs border-indigo-600 text-indigo-300">
                        ⏱ {DELAYS.find(d => d.value === data.delay)?.label || data.delay}
                    </Badge>
                )}
                {data.segmento && (
                    <Badge variant="outline" className="mt-1 text-xs border-amber-700 text-amber-300 ml-1">
                        {SEGMENTOS.find(s => s.value === data.segmento)?.label || data.segmento}
                    </Badge>
                )}
                {data.nodeType === 'condition' && (
                    <div className="flex gap-1 mt-1">
                        <Badge className="text-xs bg-green-900/40 text-green-300 border border-green-700">Sim</Badge>
                        <Badge className="text-xs bg-red-900/40 text-red-300 border border-red-700">Não</Badge>
                    </div>
                )}
            </div>

            <Handle type="source" position={Position.Bottom} className="!bg-gray-400 !border-gray-200 !w-3 !h-3" />
        </div>
    );
};

const nodeTypes = { custom: CustomNode };

// ---------------------------------------------------------------------------
// FlowEditor principal
// ---------------------------------------------------------------------------
export const FlowEditor = () => {
    const { flowId } = useParams();
    const navigate = useNavigate();

    const [flowName, setFlowName] = useState('Novo Fluxo');
    const [flowSegmento, setFlowSegmento] = useState('geral');
    const [active, setActive] = useState(false);
    const [saving, setSaving] = useState(false);

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    // Modals
    const [configOpen, setConfigOpen] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [nodeConfig, setNodeConfig] = useState({});

    // Dados externos
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [waTemplates, setWaTemplates] = useState([]);

    // ---------------------------------------------------------------------------
    useEffect(() => {
        templatesAPI.list().then(r => setEmailTemplates(r.data || [])).catch(() => {});
        whatsappAPI.listTemplates().then(r => setWaTemplates(r.data || [])).catch(() => {});

        if (flowId && flowId !== 'novo') {
            flowsAPI.get(flowId).then(res => {
                const f = res.data;
                setFlowName(f.name || 'Fluxo');
                setFlowSegmento(f.segmento || 'geral');
                setActive(f.active || false);
                const loadedNodes = (f.nodes || []).map(n => ({
                    id: n.node_id,
                    type: 'custom',
                    position: n.position || { x: 100, y: 100 },
                    data: { nodeType: n.node_type, ...n.data },
                }));
                const loadedEdges = (f.edges || []).map(e => ({
                    id: e.edge_id,
                    source: e.source,
                    target: e.target,
                    type: 'custom',
                    markerEnd: { type: MarkerType.ArrowClosed, color: '#9CA3AF' },
                    style: { stroke: '#9CA3AF' },
                    data: { onDelete: deleteEdge },
                }));
                setNodes(loadedNodes);
                setEdges(loadedEdges);
            }).catch(() => toast.error('Erro ao carregar fluxo'));
        } else {
            // Nó inicial padrão
            setNodes([{
                id: 'node-1',
                type: 'custom',
                position: { x: 300, y: 100 },
                data: { nodeType: 'trigger', trigger: 'lead_subscribed', label: 'Novo cadastro' },
            }]);
        }
    }, [flowId]);

    const deleteEdge = useCallback((edgeId) => {
        setEdges(eds => eds.filter(e => e.id !== edgeId));
    }, [setEdges]);

    const onConnect = useCallback((params) => {
        setEdges(eds => addEdge({
            ...params,
            id: `edge-${Date.now()}`,
            type: 'custom',
            markerEnd: { type: MarkerType.ArrowClosed, color: '#78716c' },
            style: { stroke: '#78716c' },
            data: { onDelete: deleteEdge },
        }, eds));
    }, [setEdges, deleteEdge]);

    // ---------------------------------------------------------------------------
    // Adicionar nó
    const addNode = (nodeType) => {
        const id = `node-${Date.now()}`;
        const config = NODE_CONFIGS[nodeType];
        const defaultData = getDefaultNodeData(nodeType);
        setNodes(nds => [...nds, {
            id,
            type: 'custom',
            position: { x: 200 + Math.random() * 200, y: 100 + nds.length * 120 },
            data: { nodeType, label: config.description, ...defaultData },
        }]);
    };

    const getDefaultNodeData = (nodeType) => {
        switch (nodeType) {
            case 'trigger':    return { trigger: 'lead_subscribed', label: 'Novo cadastro' };
            case 'wait':       return { delay: '1d', label: 'Aguardar 1 dia' };
            case 'email':      return { subject: '', body: '', templateId: '', label: 'Enviar email' };
            case 'whatsapp':   return { templateId: '', variables: {}, label: 'Enviar WhatsApp' };
            case 'condition':  return { field: 'segmento', operator: 'equals', value: '', label: 'Verificar condição' };
            case 'tag':        return { tag: '', label: 'Adicionar tag' };
            case 'filter':     return { field: 'opted_in_whatsapp', operator: 'equals', value: 'true', label: 'Filtrar contatos' };
            default:           return {};
        }
    };

    // ---------------------------------------------------------------------------
    // Deletar nó
    const deleteSelectedNode = () => {
        if (!selectedNode) return;
        setNodes(nds => nds.filter(n => n.id !== selectedNode.id));
        setEdges(eds => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
        setSelectedNode(null);
        setConfigOpen(false);
    };

    // ---------------------------------------------------------------------------
    // Abrir config do nó
    const onNodeClick = useCallback((_, node) => {
        setSelectedNode(node);
        setNodeConfig({ ...node.data });
        setConfigOpen(true);
    }, []);

    // Salvar config do nó
    const saveNodeConfig = () => {
        if (!selectedNode) return;
        const label = buildNodeLabel(nodeConfig);
        setNodes(nds => nds.map(n =>
            n.id === selectedNode.id
                ? { ...n, data: { ...nodeConfig, label } }
                : n
        ));
        setConfigOpen(false);
        toast.success('Nó atualizado');
    };

    const buildNodeLabel = (cfg) => {
        switch (cfg.nodeType) {
            case 'trigger':   return TRIGGERS.find(t => t.value === cfg.trigger)?.label || 'Gatilho';
            case 'wait':      return `Aguardar ${DELAYS.find(d => d.value === cfg.delay)?.label || cfg.delay}`;
            case 'email':     return cfg.subject ? `Email: ${cfg.subject}` : 'Enviar email';
            case 'whatsapp':  return cfg.templateId ? `WhatsApp: ${waTemplates.find(t => t.id === cfg.templateId)?.name || ''}` : 'Enviar WhatsApp';
            case 'condition': return `Se ${cfg.field} ${cfg.operator} ${cfg.value}`;
            case 'tag':       return cfg.tag ? `Tag: ${cfg.tag}` : 'Adicionar tag';
            case 'filter':    return `Filtro: ${cfg.field}`;
            default:          return 'Nó';
        }
    };

    // ---------------------------------------------------------------------------
    // Salvar fluxo
    const saveFlow = async () => {
        setSaving(true);
        try {
            const payload = {
                name: flowName,
                segmento: flowSegmento,
                active,
                nodes: nodes.map(n => ({
                    node_id: n.id,
                    node_type: n.data.nodeType,
                    position: n.position,
                    data: { ...n.data },
                })),
                edges: edges.map(e => ({
                    edge_id: e.id,
                    source: e.source,
                    target: e.target,
                    data: {},
                })),
            };

            if (flowId && flowId !== 'novo') {
                await flowsAPI.update(flowId, payload);
                toast.success('Fluxo salvo');
            } else {
                const res = await flowsAPI.create(payload);
                toast.success('Fluxo criado');
                navigate(`/flows/${res.data.id}`, { replace: true });
            }
        } catch (e) {
            toast.error('Erro ao salvar fluxo');
        } finally {
            setSaving(false);
        }
    };

    // Toggle ativo
    const toggleActive = async () => {
        if (!flowId || flowId === 'novo') { toast.error('Salve o fluxo primeiro'); return; }
        try {
            if (active) {
                await flowsAPI.deactivate(flowId);
                setActive(false);
                toast.success('Fluxo pausado');
            } else {
                await flowsAPI.activate(flowId);
                setActive(true);
                toast.success('Fluxo ativado');
            }
        } catch { toast.error('Erro ao alterar status'); }
    };

    // ---------------------------------------------------------------------------
    // Render do painel de configuração do nó
    const renderNodeConfig = () => {
        if (!selectedNode) return null;
        const type = nodeConfig.nodeType;

        return (
            <div className="space-y-4">
                {/* Trigger */}
                {type === 'trigger' && (
                    <div className="space-y-2">
                        <Label className="text-gray-700">Gatilho</Label>
                        <Select value={nodeConfig.trigger} onValueChange={v => setNodeConfig(c => ({ ...c, trigger: v }))}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                                {TRIGGERS.map(t => (
                                    <SelectItem key={t.value} value={t.value} className="text-gray-800">{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Label className="text-gray-700">Segmento alvo</Label>
                        <Select value={nodeConfig.segmento || 'geral'} onValueChange={v => setNodeConfig(c => ({ ...c, segmento: v }))}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                                {SEGMENTOS.map(s => (
                                    <SelectItem key={s.value} value={s.value} className="text-gray-800">{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {/* Wait */}
                {type === 'wait' && (
                    <div className="space-y-2">
                        <Label className="text-gray-700">Tempo de espera</Label>
                        <Select value={nodeConfig.delay} onValueChange={v => setNodeConfig(c => ({ ...c, delay: v }))}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                                {DELAYS.map(d => (
                                    <SelectItem key={d.value} value={d.value} className="text-gray-800">{d.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {/* Email */}
                {type === 'email' && (
                    <div className="space-y-2">
                        <Label className="text-gray-700">Template de email</Label>
                        <Select value={nodeConfig.templateId || ''} onValueChange={v => setNodeConfig(c => ({ ...c, templateId: v }))}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                                <SelectValue placeholder="Selecionar template" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                                {emailTemplates.map(t => (
                                    <SelectItem key={t.id} value={t.id} className="text-gray-800">{t.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Label className="text-gray-700">Assunto (opcional)</Label>
                        <Input className="bg-stone-800 border-stone-700 text-stone-100"
                            value={nodeConfig.subject || ''} placeholder="Assunto do email"
                            onChange={e => setNodeConfig(c => ({ ...c, subject: e.target.value }))} />
                        <Label className="text-gray-700">Conteúdo</Label>
                        <Textarea className="bg-gray-50 border-gray-200 text-gray-900 h-28" rows={5}
                            value={nodeConfig.body || ''} placeholder="Corpo do email (HTML ou texto)"
                            onChange={e => setNodeConfig(c => ({ ...c, body: e.target.value }))} />
                    </div>
                )}

                {/* WhatsApp */}
                {type === 'whatsapp' && (
                    <div className="space-y-2">
                        <Label className="text-gray-700">Template WhatsApp (Meta Cloud API)</Label>
                        <Select value={nodeConfig.templateId || ''} onValueChange={v => setNodeConfig(c => ({ ...c, templateId: v }))}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                                <SelectValue placeholder="Selecionar template aprovado" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                                {waTemplates.length === 0 && (
                                    <SelectItem value="_none" disabled className="text-gray-400">
                                        Nenhum template cadastrado
                                    </SelectItem>
                                )}
                                {waTemplates.map(t => (
                                    <SelectItem key={t.id} value={t.id} className="text-gray-800">
                                        {t.name} — {t.category}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-stone-500">
                            Templates precisam ser aprovados na Meta antes de usar.
                        </p>
                    </div>
                )}

                {/* Condition */}
                {type === 'condition' && (
                    <div className="space-y-2">
                        <Label className="text-gray-700">Campo</Label>
                        <Select value={nodeConfig.field || 'segmento'} onValueChange={v => setNodeConfig(c => ({ ...c, field: v }))}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                                <SelectItem value="segmento" className="text-gray-800">Segmento</SelectItem>
                                <SelectItem value="status" className="text-gray-800">Status do lead</SelectItem>
                                <SelectItem value="opted_in_whatsapp" className="text-gray-800">Opt-in WhatsApp</SelectItem>
                                <SelectItem value="opted_in_email" className="text-gray-800">Opt-in Email</SelectItem>
                                <SelectItem value="tags" className="text-gray-800">Tags</SelectItem>
                            </SelectContent>
                        </Select>
                        <Label className="text-gray-700">Operador</Label>
                        <Select value={nodeConfig.operator || 'equals'} onValueChange={v => setNodeConfig(c => ({ ...c, operator: v }))}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                                <SelectItem value="equals" className="text-gray-800">igual a</SelectItem>
                                <SelectItem value="not_equals" className="text-gray-800">diferente de</SelectItem>
                                <SelectItem value="contains" className="text-gray-800">contém</SelectItem>
                                <SelectItem value="is_true" className="text-gray-800">é verdadeiro</SelectItem>
                            </SelectContent>
                        </Select>
                        <Label className="text-gray-700">Valor</Label>
                        <Input className="bg-stone-800 border-stone-700 text-stone-100"
                            value={nodeConfig.value || ''} placeholder="ex: mesa_de_ester"
                            onChange={e => setNodeConfig(c => ({ ...c, value: e.target.value }))} />
                    </div>
                )}

                {/* Tag */}
                {type === 'tag' && (
                    <div className="space-y-2">
                        <Label className="text-gray-700">Tag</Label>
                        <Input className="bg-stone-800 border-stone-700 text-stone-100"
                            value={nodeConfig.tag || ''} placeholder="ex: mentoria-2026"
                            onChange={e => setNodeConfig(c => ({ ...c, tag: e.target.value }))} />
                    </div>
                )}

                {/* Filter */}
                {type === 'filter' && (
                    <div className="space-y-2">
                        <Label className="text-gray-700">Campo</Label>
                        <Select value={nodeConfig.field || 'opted_in_whatsapp'} onValueChange={v => setNodeConfig(c => ({ ...c, field: v }))}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-gray-200">
                                <SelectItem value="opted_in_whatsapp" className="text-gray-800">Opt-in WhatsApp</SelectItem>
                                <SelectItem value="opted_in_email" className="text-gray-800">Opt-in Email</SelectItem>
                                <SelectItem value="segmento" className="text-gray-800">Segmento</SelectItem>
                                <SelectItem value="status" className="text-gray-800">Status</SelectItem>
                            </SelectContent>
                        </Select>
                        <Label className="text-gray-700">Valor esperado</Label>
                        <Input className="bg-stone-800 border-stone-700 text-stone-100"
                            value={nodeConfig.value || 'true'} placeholder="true / false / valor"
                            onChange={e => setNodeConfig(c => ({ ...c, value: e.target.value }))} />
                    </div>
                )}
            </div>
        );
    };

    // ---------------------------------------------------------------------------
    return (
        <div className="h-screen flex flex-col bg-white">
            {/* Toolbar */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/flows')}
                        className="text-gray-500 hover:text-gray-900">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
                    </Button>
                    <div className="w-px h-5 bg-gray-200" />
                    <Input
                        value={flowName}
                        onChange={e => setFlowName(e.target.value)}
                        className="bg-transparent border-none text-gray-900 font-bold text-base w-64 focus-visible:ring-0 p-0"
                        placeholder="Nome do fluxo"
                    />
                    <Select value={flowSegmento} onValueChange={setFlowSegmento}>
                        <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-700 w-52 h-8 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                            {SEGMENTOS.map(s => (
                                <SelectItem key={s.value} value={s.value} className="text-gray-800 text-xs">{s.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-center gap-2">
                    {/* Adicionar nós */}
                    <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1 bg-gray-50">
                        {Object.entries(NODE_CONFIGS).map(([key, cfg]) => {
                            const Icon = cfg.icon;
                            return (
                                <button key={key} onClick={() => addNode(key)} title={`Adicionar ${cfg.label}`}
                                    className="p-1.5 rounded hover:bg-white hover:shadow-sm transition-colors group relative">
                                    <Icon className="w-4 h-4 text-gray-400 group-hover:text-gray-700" />
                                    <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-xs bg-gray-800 text-white px-2 py-0.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
                                        {cfg.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <div className="w-px h-6 bg-gray-200" />

                    <Button variant="outline" size="sm" onClick={toggleActive}
                        className={`border-gray-200 text-sm ${active ? 'text-green-600 border-green-300 bg-green-50' : 'text-gray-500'}`}>
                        {active ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pausar</> : <><Play className="w-3.5 h-3.5 mr-1" /> Ativar</>}
                    </Button>

                    <Button size="sm" onClick={saveFlow} disabled={saving}
                        className="bg-amber-700 hover:bg-amber-600 text-white">
                        <Save className="w-3.5 h-3.5 mr-1" />
                        {saving ? 'Salvando…' : 'Salvar'}
                    </Button>
                </div>
            </div>

            {/* Canvas */}
            <div className="flex-1">
                <ReactFlow
                    nodes={nodes} edges={edges}
                    onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
                    onConnect={onConnect} onNodeClick={onNodeClick}
                    nodeTypes={nodeTypes} edgeTypes={edgeTypes}
                    fitView
                    style={{ background: '#F8F9FA' }}
                >
                    <Background color="#E5E7EB" gap={24} />
                    <Controls className="!bg-white !border-gray-200 !rounded-xl" />
                    <MiniMap nodeColor="#9CA3AF" maskColor="rgba(243,244,246,0.8)"
                        className="!bg-white !border-gray-200 !rounded-xl" />

                    {/* Legenda */}
                    <Panel position="bottom-left">
                        <Card className="bg-white/95 border-gray-200 backdrop-blur shadow-sm">
                            <CardContent className="p-3">
                                <p className="text-xs text-gray-500 font-medium mb-2">Tipos de nó</p>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                    {Object.entries(NODE_CONFIGS).map(([key, cfg]) => {
                                        const Icon = cfg.icon;
                                        return (
                                            <div key={key} className="flex items-center gap-1.5">
                                                <Icon className="w-3 h-3" style={{ color: cfg.color }} />
                                                <span className="text-xs text-gray-600">{cfg.label}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </Panel>
                </ReactFlow>
            </div>

            {/* Modal de configuração do nó */}
            <Dialog open={configOpen} onOpenChange={setConfigOpen}>
                <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-gray-900">
                            {selectedNode && (() => {
                                const cfg = NODE_CONFIGS[selectedNode.data.nodeType];
                                const Icon = cfg?.icon;
                                return <><Icon className="w-4 h-4" style={{ color: cfg?.color }} /> {cfg?.label}</>;
                            })()}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {renderNodeConfig()}

                        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                            <Button variant="ghost" size="sm" onClick={deleteSelectedNode}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50">
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> Remover nó
                            </Button>
                            <div className="flex gap-2">
                                <Button variant="ghost" size="sm" onClick={() => setConfigOpen(false)}
                                    className="text-gray-500">Cancelar</Button>
                                <Button size="sm" onClick={saveNodeConfig}
                                    className="bg-amber-700 hover:bg-amber-600 text-white">Aplicar</Button>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default FlowEditor;
